import { useState, useCallback, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';

/**
 * Hook for managing PDF report generation - async flow via Edge Function
 */
export function useReports(organisationId, organisationName = null, userData = null) {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pendingJob, setPendingJob] = useState(null);

  // Fetch user's reports
  const fetchReports = useCallback(async () => {
    if (!organisationId) {
      setReports([]);
      setLoading(false);
      return [];
    }

    setLoading(true);
    try {
      // Fetch reports using organisation ID for strict isolation
      const { data, error } = await supabase
        .from('report_jobs')
        .select('*, user_name, user_email, output_format')
        .eq('organisation_id', organisationId)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;

      // Transform data for display
      const transformedReports = (data || []).map(job => ({
        ...job,
        report_name: job.report_name || `Emissions Report FY ${job.financial_year || job.created_at?.slice(0, 4) || 'N/A'}`,
        organisation_name: organisationName || organisationId
      }));

      setReports(transformedReports);

      // Check for any pending jobs
      const pending = data?.find(r => r.status === 'pending' || r.status === 'processing');
      setPendingJob(pending || null);

      return transformedReports;
    } catch (err) {
      console.error('fetchReports error:', err);
      setError(err.message);
      return [];
    } finally {
      setLoading(false);
    }
  }, [organisationId, organisationName]);

  // Request new PDF report via Edge Function
  const requestReport = useCallback(async (year, reportType = 'emissions_pdf', userName = null, isSample = false) => {
    if (!organisationId) {
      return { error: 'No organisation selected' };
    }

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      // Check for existing pending job first (client-side check)
      const { data: existingJob } = await supabase
        .from('report_jobs')
        .select('id, status')
        .eq('organisation_id', organisationId)
        .eq('report_type', reportType)
        .in('status', ['pending', 'processing'])
        .maybeSingle();

      if (existingJob) {
        return {
          success: false,
          error: 'A report is already being generated',
          job_id: existingJob.id
        };
      }

      // Get Supabase URL and anon key from environment
      const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
      const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
      // Use fetch with explicit CORS mode
      const response = await fetch(`${supabaseUrl}/functions/v1/request-pdf`, {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          user_id: session.user.id,
          organisation_id: organisationId,
          financial_year: year,
          report_type: reportType,
          user_email: session.user.email,
          user_name: userName || session.user.user_metadata?.full_name,
          organisation_name: organisationName,
          is_sample: isSample
        })
      });
      // Read response text first, then parse as JSON
      const responseText = await response.text();
      let result;
      try {
        result = JSON.parse(responseText);
      } catch (e) {
        console.error('Failed to parse JSON:', e);
        throw new Error('Invalid response from server');
      }

      if (!response.ok) {
        // Handle 409 conflict (job already exists)
        if (response.status === 409) {
          setPendingJob({ id: result.job_id, status: result.status });
          return {
            success: false,
            error: result.error || 'A report is already being generated',
            job_id: result.job_id
          };
        }
        throw new Error(result.error || `Request failed with status ${response.status}`);
      }

      setPendingJob({ id: result.job_id, status: 'pending' });
      await fetchReports();

      return { success: true, job_id: result.job_id, message: result.message };
    } catch (err) {
      console.error('requestReport error:', err);
      return { success: false, error: err.message };
    }
  }, [organisationId, organisationName, fetchReports]);

  // Get download URL for a report
  // Always use signed URLs for security (bucket is private)
  const getDownloadUrl = useCallback(async (report) => {
    // Use file_path for signed URL
    const filePath = report.file_path;
    if (!filePath) {
      console.error('No file_path available for report');
      return null;
    }

    try {
      const { data, error } = await supabase.storage
        .from('reports')
        .createSignedUrl(filePath, 3600); // 1 hour expiry

      if (error) throw error;
      return data.signedUrl;
    } catch (err) {
      console.error('Error getting download URL:', err);
      return null;
    }
  }, []);

  // Delete a report
  const deleteReport = useCallback(async (reportId, filePath) => {
    try {
      // Delete file from storage if exists
      if (filePath) {
        await supabase.storage.from('reports').remove([filePath]);
      }

      // Delete record
      const { error } = await supabase
        .from('report_jobs')
        .delete()
        .eq('id', reportId);

      if (error) throw error;

      setReports(prev => prev.filter(r => r.id !== reportId));
      return { error: null };
    } catch (err) {
      return { error: err.message };
    }
  }, []);

  // Subscribe to report status changes + polling fallback
  useEffect(() => {
    if (!pendingJob) return;

    // Real-time subscription
    const subscription = supabase
      .channel(`report-status-${pendingJob.id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'report_jobs',
          filter: `id=eq.${pendingJob.id}`
        },
        (payload) => {
          const updated = payload.new;

          if (updated.status === 'completed' || updated.status === 'failed') {
            setPendingJob(null);
            fetchReports();
          } else {
            setPendingJob(updated);
          }
        }
      )
      .subscribe();

    // Polling fallback - check every 5 seconds in case real-time doesn't fire
    const pollInterval = setInterval(async () => {
      const { data } = await supabase
        .from('report_jobs')
        .select('status')
        .eq('id', pendingJob.id)
        .single();

      if (data && (data.status === 'completed' || data.status === 'failed')) {
        setPendingJob(null);
        fetchReports();
      }
    }, 5000);

    return () => {
      subscription.unsubscribe();
      clearInterval(pollInterval);
    };
  }, [pendingJob, fetchReports]);

  // Initial fetch
  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  return {
    reports,
    loading,
    error,
    pendingJob,
    fetchReports,
    requestReport,
    getDownloadUrl,
    deleteReport,
    hasPendingReport: !!pendingJob
  };
}

export default useReports;
